// formulaAdapter.js — traduzione pura tra JSON backend e forma React Flow.
// È l'UNICO punto che conosce il formato backend. Nessuna dipendenza da React.

const TIPO_TO_RF = { Squadra: 'team', Girone: 'girone', Classifica: 'classifica', Dirette: 'dirette' };
const RF_TO_TIPO = { team: 'Squadra', girone: 'Girone', classifica: 'Classifica', dirette: 'Dirette' };

export const rfTypeFromTipo = (tipo) => TIPO_TO_RF[tipo] || 'girone';
export const tipoFromRfType = (rfType) => RF_TO_TIPO[rfType] || 'Girone';

// Asimmetria del nodo Dirette nei nomi dei link backend:
// come SORGENTE è "Dirette" (DiretteToClassifica), come DESTINAZIONE è "Diretta"
// (GironeToDiretta, ClassificaToDiretta).
const SOURCE_KIND = { team: 'Squadra', girone: 'Girone', classifica: 'Classifica', dirette: 'Dirette' };
const TARGET_KIND = { team: 'Squadra', girone: 'Girone', classifica: 'Classifica', dirette: 'Diretta' };
export const sourceKind = (node) => SOURCE_KIND[node.type] || 'Girone';
export const targetKind = (node) => TARGET_KIND[node.type] || 'Girone';

export const LINK_TYPE = (fromKind, toKind) => `${fromKind}To${toKind}`;

export const VALID_LINK_TYPES = new Set([
  'SquadraToGirone', 'SquadraToClassifica',
  'GironeToGirone', 'GironeToDiretta', 'GironeToClassifica',
  'DiretteToClassifica',
  'ClassificaToGirone', 'ClassificaToDiretta', 'ClassificaToClassifica',
]);

const NUMBERED_TYPES = new Set([
  'GironeToGirone', 'GironeToDiretta', 'GironeToClassifica', 'DiretteToClassifica',
  'SquadraToClassifica', 'ClassificaToGirone', 'ClassificaToDiretta', 'ClassificaToClassifica',
]);
export const isNumberedType = (type) => NUMBERED_TYPES.has(type);

const defaultPos = (fase, index) => ({
  x: fase.XPos !== undefined ? fase.XPos : (fase.tipo === 'Squadra' ? 50 : 300 + index * 50),
  y: fase.YPos !== undefined ? fase.YPos : (fase.tipo === 'Squadra' ? 50 + index * 45 : 200 + index * 100),
});

export const backendToFlow = (data) => {
  const fasi = Array.isArray(data?.fasi) ? data.fasi : [];
  const links = Array.isArray(data?.links) ? data.links : [];

  const nodes = fasi.map((fase, index) => {
    const base = {
      id: `node-${fase.id}`,
      type: rfTypeFromTipo(fase.tipo),
      position: defaultPos(fase, index),
      data: {
        originalId: fase.id,
        name: fase.name,
        tipo: fase.tipo,
        incomingConnectionsOrder: {},
      },
    };
    if (fase.tipo === 'Squadra') {
      base.data.idSquadra = fase.idSquadra ?? null;
    } else if (fase.tipo === 'Classifica') {
      base.data.sortPriority = Array.isArray(fase.sortPriority) && fase.sortPriority.length > 0
        ? fase.sortPriority : null;
    } else if (fase.tipo === 'Dirette') {
      base.data.expanded = fase.expanded ?? true;
      base.data.width = fase.width ?? 300;
      base.data.height = fase.height ?? 200;
      base.data.fascia = fase.fascia ?? 1;
      base.data.internalMatches = fase.internalMatches ?? [];
      base.data.internalConnections = fase.internalConnections ?? [];
      base.data.inputMapping = fase.inputMapping ?? [];
      base.data.outputMapping = fase.outputMapping ?? [];
    }
    return base;
  });

  const edges = links.map((link) => ({
    id: `conn-${link.id}`,
    source: `node-${link.fromId}`,
    target: `node-${link.toId}`,
    type: 'formulaEdge',
    data: {
      originalId: link.id,
      linkType: link.type,
      fromPosition: link.fromPosition,
      toPosition: link.toPosition ?? null,
      connectionNumber: isNumberedType(link.type) ? (link.fromPosition || 1) : 0,
    },
  }));

  nodes.forEach((node) => {
    const incoming = edges.filter((e) => e.target === node.id);
    if (incoming.length === 0) return;
    const orderMap = {};
    incoming.forEach((e) => {
      if (e.data.toPosition !== undefined && e.data.toPosition !== null) {
        orderMap[e.id] = e.data.toPosition;
      }
    });
    if (Object.keys(orderMap).length === 0) {
      incoming.forEach((e, i) => { orderMap[e.id] = i; });
    }
    node.data.incomingConnectionsOrder = orderMap;
  });

  const sortPriority = Array.isArray(data?.sortPriority) && data.sortPriority.length > 0
    ? data.sortPriority : null;

  // I gruppi-fase non si editano qui (l'interfaccia sta sul canvas storico), ma vanno conservati
  // e ri-salvati: un giro su questo canvas non deve cancellarli.
  const gruppiFase = Array.isArray(data?.gruppiFase) && data.gruppiFase.length > 0
    ? data.gruppiFase : null;

  return { nodes, edges, sortPriority, gruppiFase };
};

const idFromNode = (node) => node.data?.originalId ?? parseInt(String(node.id).replace('node-', ''), 10);
const idFromEdge = (edge) => edge.data?.originalId ?? parseInt(String(edge.id).replace('conn-', ''), 10);

export const flowToBackend = (nodes, edges, sortPriority, gruppiFase) => {
  const fasi = nodes.map((node) => {
    const tipo = node.data?.tipo ?? tipoFromRfType(node.type);
    const base = {
      id: idFromNode(node),
      name: node.data?.name,
      tipo,
      XPos: node.position.x,
      YPos: node.position.y,
    };
    if (tipo === 'Squadra') {
      return { ...base, idSquadra: node.data?.idSquadra ?? null };
    }
    if (tipo === 'Classifica') {
      return Array.isArray(node.data?.sortPriority) && node.data.sortPriority.length > 0
        ? { ...base, sortPriority: node.data.sortPriority }
        : base;
    }
    if (tipo === 'Dirette') {
      return {
        ...base,
        expanded: node.data?.expanded,
        width: node.data?.width,
        height: node.data?.height,
        fascia: node.data?.fascia ?? 1,
        internalMatches: node.data?.internalMatches ?? [],
        internalConnections: node.data?.internalConnections ?? [],
        inputMapping: node.data?.inputMapping ?? [],
        outputMapping: node.data?.outputMapping ?? [],
      };
    }
    return base;
  });

  const links = edges.map((edge) => {
    const type = edge.data?.linkType || 'SquadraToGirone';
    const targetNode = nodes.find((n) => n.id === edge.target);
    const base = {
      id: idFromEdge(edge),
      fromId: parseInt(String(edge.source).replace('node-', ''), 10),
      toId: parseInt(String(edge.target).replace('node-', ''), 10),
      type,
    };

    if (type === 'SquadraToGirone') {
      base.fromPosition = 1;
    } else if (isNumberedType(type)) {
      base.fromPosition = edge.data?.connectionNumber || 1;
    } else {
      base.fromPosition = edge.data?.fromPosition || 1;
    }

    if (edge.data?.toPosition !== undefined && edge.data?.toPosition !== null) {
      base.toPosition = edge.data.toPosition;
    } else if (targetNode) {
      const incoming = edges.filter((e) => e.target === targetNode.id);
      const orderMap = targetNode.data?.incomingConnectionsOrder || {};
      const sorted = incoming
        .map((e) => ({ id: e.id, order: orderMap[e.id] || 0 }))
        .sort((a, b) => a.order - b.order);
      const pos = sorted.findIndex((e) => e.id === edge.id);
      base.toPosition = pos >= 0 ? pos + 1 : 1;
    } else {
      base.toPosition = 1;
    }

    return base;
  });

  const result = { fasi, links };
  if (Array.isArray(sortPriority) && sortPriority.length > 0) {
    result.sortPriority = sortPriority;
  }
  if (Array.isArray(gruppiFase) && gruppiFase.length > 0) {
    const idEsistenti = new Set(nodes.map(idFromNode));
    const puliti = gruppiFase
      .map((g) => ({ ...g, nodi: (g.nodi || []).filter((id) => idEsistenti.has(id)) }))
      .filter((g) => g.nodi.length > 0);
    if (puliti.length > 0) {
      result.gruppiFase = puliti;
    }
  }
  return result;
};
